import { Controller } from "@hotwired/stimulus";

// Scroll-reveals its items: they fade and rise into place as they enter the
// viewport, lightly staggered. The content is visible by default (in CSS), so
// without JS, or when the visitor prefers reduced motion, nothing is ever
// hidden — this controller only ADDS motion, it never gates content. The hidden
// start state (.reveal-pending) is applied here only when motion is allowed,
// and the transition itself is CSS (24-motion.css), so no animation library
// ships in the bundle.
export default class extends Controller {
  static targets = ["item"];
  static values = {
    y: { type: Number, default: 24 },
    duration: { type: Number, default: 0.6 },
    stagger: { type: Number, default: 0.12 },
  };

  connect() {
    // Turbo shows a cached preview before the real response; skip it so the
    // reveal only runs once, on the real page (matches impression_controller).
    if (document.documentElement.hasAttribute("data-turbo-preview")) {return;}

    this.items = this.hasItemTarget ? this.itemTargets : [this.element];

    // Reduced motion: leave everything visible, just mark ready.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.markReady();
      return;
    }

    this.items.forEach((item, index) => {
      item.style.setProperty("--reveal-y", `${this.yValue}px`);
      item.style.setProperty("--reveal-duration", `${this.durationValue}s`);
      item.style.setProperty("--reveal-delay", `${index * this.staggerValue}s`);
      item.classList.add("reveal-pending");
    });

    // Fire once the section's top crosses 85% of the viewport height (the old
    // ScrollTrigger `start: "top 85%"`), and only once.
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {return;}

      this.observer.disconnect();
      this.observer = null;
      // Let the pending state paint first so the transition has a start frame.
      requestAnimationFrame(() => this.reveal());
    }, { rootMargin: "0px 0px -15% 0px" });
    this.observer.observe(this.element);

    this.markReady();
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = null;
    // Never leave content faded out if torn down mid-reveal (Turbo navigation).
    this.items?.forEach((item) => item.classList.remove("reveal-pending", "reveal-visible"));
  }

  reveal() {
    this.items?.forEach((item) => item.classList.add("reveal-visible"));
  }

  // Lets system specs wait until the controller has wired up.
  markReady() {
    this.element.setAttribute("data-reveal-ready", "");
  }
}
